import React, { useState, useEffect, useRef, useCallback } from "react"
import { makeStyles } from "tss-react/mui"
import {
  Typography,
  Select,
  MenuItem,
  IconButton,
  Switch,
  Tooltip,
} from "@mui/material"
import {
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material"

/**
 * Device picker for the Settings → Voice & Video pane. Mirrors DeviceSetupModal
 * (camera + mic dropdowns, start-on-join toggles) but WITHOUT the video preview
 * and without the Dialog chrome — it lives inline in the settings pane.
 *
 * Unlike the modal (which has an OK button), this commits on every user change:
 * each device pick / toggle calls onConfirm with the full settings object. The
 * parent's onConfirm (App.handleSetupConfirm) persists to localStorage AND, if
 * currently broadcasting, restarts the publish with the new device.
 *
 * To populate device LABELS the browser requires a prior media-permission grant,
 * so on mount we briefly acquire a stream, read the labels, then release it
 * immediately (no preview shown). Initial enumeration does NOT call onConfirm —
 * only explicit user changes do, so merely opening the pane never re-publishes.
 *
 * Props:
 *  - initialSettings : saved deviceSetup ({ videoDeviceId, audioDeviceId,
 *                      videoLabel, audioLabel, videoOn, micOn, dontShowAgain })
 *                      or null.
 *  - onConfirm(settings) : called on each change with the merged settings.
 *  - isGuest : guests broadcast at default; they still pick devices here.
 */
const useStyles = makeStyles()((theme) => ({
  deviceRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
    maxWidth: 420,
  },
  deviceIcon: {
    color: theme.palette.text.secondary,
    flexShrink: 0,
  },
  select: {
    flex: 1,
    minWidth: 0,
  },
  refreshBtn: {
    flexShrink: 0,
  },
  toggleRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    maxWidth: 420,
  },
  toggleLabel: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  toggleTitle: {
    fontSize: "0.9rem",
    color: theme.palette.text.primary,
  },
  toggleSub: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
  },
  errorText: {
    color: theme.palette.error.main,
    fontSize: "0.85rem",
    marginBottom: theme.spacing(1),
    maxWidth: 420,
  },
}))

// Stop every track on a stream (releases the camera/mic). Safe on null.
const stopStream = (stream) => {
  if (!stream) return
  stream.getTracks().forEach((t) => t.stop())
}

const DeviceSettingsPanel = ({ initialSettings = null, onConfirm, isGuest = false }) => {
  const { classes } = useStyles()

  const [videoInputs, setVideoInputs] = useState([])
  const [audioInputs, setAudioInputs] = useState([])
  const [videoDeviceId, setVideoDeviceId] = useState(initialSettings?.videoDeviceId || "")
  const [audioDeviceId, setAudioDeviceId] = useState(initialSettings?.audioDeviceId || "")
  const [videoOn, setVideoOn] = useState(initialSettings?.videoOn !== false)
  const [micOn, setMicOn] = useState(initialSettings?.micOn !== false)
  const [error, setError] = useState(null)

  // Latest values, readable inside async callbacks without re-creating them.
  const stateRef = useRef({ videoDeviceId, audioDeviceId, videoOn, micOn })
  stateRef.current = { videoDeviceId, audioDeviceId, videoOn, micOn }

  // Clamp a Select value to an id that actually exists in the options (browser
  // "default" pseudo-device / stale id after unplug would otherwise warn).
  const safeValue = (id, options) =>
    options.some((d) => d.deviceId === id) ? id : options[0]?.deviceId || ""

  // Build the merged settings object to hand back to the parent on a change.
  // Preserves dontShowAgain from the saved settings (editing devices here must
  // not flip that flag) and attaches device labels (the stable key the publish
  // path uses to re-resolve a rotated deviceId).
  const buildSettings = useCallback(
    (over) => {
      const cur = { ...stateRef.current, ...over }
      const vId = safeValue(cur.videoDeviceId, videoInputs)
      const aId = safeValue(cur.audioDeviceId, audioInputs)
      return {
        videoDeviceId: vId,
        audioDeviceId: aId,
        videoLabel: videoInputs.find((d) => d.deviceId === vId)?.label || "",
        audioLabel: audioInputs.find((d) => d.deviceId === aId)?.label || "",
        videoOn: cur.videoOn,
        micOn: cur.micOn,
        // Keep whatever the user last chose for the join prompt suppression.
        dontShowAgain: initialSettings?.dontShowAgain === true,
      }
    },
    [videoInputs, audioInputs, initialSettings]
  )

  const commit = useCallback(
    (over) => {
      onConfirm && onConfirm(buildSettings(over))
    },
    [onConfirm, buildSettings]
  )

  // Briefly acquire a stream so device labels populate, then release it. No
  // preview is shown. Audio is always requested; video only when videoOn so the
  // camera light doesn't stay on. Falls back to non-exact constraints if a saved
  // deviceId is stale. Returns the active deviceIds for dropdown sync.
  const enumerateWithLabels = useCallback(async () => {
    setError(null)
    const { videoDeviceId: vid, audioDeviceId: aud, videoOn: von } = stateRef.current
    const build = (exact) => ({
      video: von
        ? { ...(exact && vid ? { deviceId: { exact: vid } } : {}) }
        : false,
      audio: { ...(exact && aud ? { deviceId: { exact: aud } } : {}) },
    })
    let stream = null
    try {
      try {
        stream = await navigator.mediaDevices.getUserMedia(build(true))
      } catch {
        stream = await navigator.mediaDevices.getUserMedia(build(false))
      }
    } catch (err) {
      // Permission denied / no devices: we can still enumerate (generic labels).
      console.warn("⚠️ device settings: could not get media:", err.message)
      setError(
        "Couldn't access your camera/microphone. Check browser permissions — you can still pick devices, but names may be hidden."
      )
    }
    try {
      const devices = await navigator.mediaDevices.enumerateDevices()
      const cams = devices.filter((d) => d.kind === "videoinput")
      const mics = devices.filter((d) => d.kind === "audioinput")
      setVideoInputs(cams)
      setAudioInputs(mics)
      // Prefer the saved id if still present; else the live track's device; else first.
      const activeVid = stream?.getVideoTracks?.()[0]?.getSettings?.().deviceId
      const activeAud = stream?.getAudioTracks?.()[0]?.getSettings?.().deviceId
      const savedVidValid = vid && cams.some((d) => d.deviceId === vid)
      const savedAudValid = aud && mics.some((d) => d.deviceId === aud)
      setVideoDeviceId((savedVidValid && vid) || activeVid || cams[0]?.deviceId || "")
      setAudioDeviceId((savedAudValid && aud) || activeAud || mics[0]?.deviceId || "")
    } catch (err) {
      console.warn("⚠️ device settings: enumerateDevices failed:", err.message)
    } finally {
      stopStream(stream) // release immediately — no preview
    }
  }, [])

  // On mount: populate the device lists + labels. Does NOT commit (opening the
  // pane shouldn't re-publish); only explicit changes below do.
  useEffect(() => {
    enumerateWithLabels()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleCameraChange = (id) => {
    setVideoDeviceId(id)
    commit({ videoDeviceId: id })
  }
  const handleMicChange = (id) => {
    setAudioDeviceId(id)
    commit({ audioDeviceId: id })
  }
  const handleToggleVideo = () => {
    const next = !videoOn
    setVideoOn(next)
    commit({ videoOn: next })
  }
  const handleToggleMic = () => {
    const next = !micOn
    setMicOn(next)
    commit({ micOn: next })
  }

  const deviceLabel = (d, i, kind) => d.label || `${kind} ${i + 1}`
  const videoValue = safeValue(videoDeviceId, videoInputs)
  const audioValue = safeValue(audioDeviceId, audioInputs)

  return (
    <>
      {error && <Typography className={classes.errorText}>{error}</Typography>}

      <div className={classes.deviceRow}>
        <VideocamIcon className={classes.deviceIcon} fontSize="small" />
        <Select
          className={classes.select}
          variant="outlined"
          margin="dense"
          displayEmpty
          value={videoValue}
          onChange={(e) => handleCameraChange(e.target.value)}
        >
          {videoInputs.length === 0 && <MenuItem value="">No cameras found</MenuItem>}
          {videoInputs.map((d, i) => (
            <MenuItem key={d.deviceId} value={d.deviceId}>
              {deviceLabel(d, i, "Camera")}
            </MenuItem>
          ))}
        </Select>
        <Tooltip title="Refresh device list">
          <IconButton
            className={classes.refreshBtn}
            size="small"
            onClick={() => enumerateWithLabels()}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.deviceRow}>
        <MicIcon className={classes.deviceIcon} fontSize="small" />
        <Select
          className={classes.select}
          variant="outlined"
          margin="dense"
          displayEmpty
          value={audioValue}
          onChange={(e) => handleMicChange(e.target.value)}
        >
          {audioInputs.length === 0 && <MenuItem value="">No microphones found</MenuItem>}
          {audioInputs.map((d, i) => (
            <MenuItem key={d.deviceId} value={d.deviceId}>
              {deviceLabel(d, i, "Microphone")}
            </MenuItem>
          ))}
        </Select>
      </div>

      <div className={classes.toggleRow}>
        {videoOn ? (
          <VideocamIcon className={classes.deviceIcon} fontSize="small" />
        ) : (
          <VideocamOffIcon className={classes.deviceIcon} fontSize="small" />
        )}
        <div className={classes.toggleLabel}>
          <span className={classes.toggleTitle}>Camera on join</span>
          <span className={classes.toggleSub}>
            {videoOn ? "Joins with your camera live" : "Joins with your camera off"}
          </span>
        </div>
        <Switch
          checked={videoOn}
          onChange={handleToggleVideo}
          color="primary"
          slotProps={{ input: { "aria-label": "Start with camera on" } }}
        />
      </div>

      <div className={classes.toggleRow}>
        {micOn ? (
          <MicIcon className={classes.deviceIcon} fontSize="small" />
        ) : (
          <MicOffIcon className={classes.deviceIcon} fontSize="small" />
        )}
        <div className={classes.toggleLabel}>
          <span className={classes.toggleTitle}>Microphone on join</span>
          <span className={classes.toggleSub}>
            {micOn ? "Joins with your mic live" : "Joins muted — unmute anytime"}
          </span>
        </div>
        <Switch
          checked={micOn}
          onChange={handleToggleMic}
          color="primary"
          slotProps={{ input: { "aria-label": "Start with microphone on" } }}
        />
      </div>
    </>
  )
}

export default DeviceSettingsPanel
